import { useContext, useEffect, useRef } from 'react'

import { AuthContext } from 'features/auth'
import { useProjectStore } from 'features/project/store'
import mixpanel from 'trackers/mixpanel'
import { ProjectState } from 'types/project'

/**
 * Handles project-level Mixpanel identification and group assignment.
 *
 * - For public (unauthenticated) users: creates or retrieves a stable anonymous ID
 *   (UUID v4) stored in localStorage and identifies it with Mixpanel.
 * - For authenticated non-Yogi users: sets two Mixpanel group keys:
 *   - `hub_uuid`: the project UUID (project_table.uuid) — one value per hub.
 *     Group display_name is set to project_table.title.
 *   - `account_uuid`: the stable UUID from account_table, joined via
 *     project_table.account. Multiple hubs belonging to the same account share the
 *     same `account_uuid`, making this the reliable group key for account-level
 *     analytics. The group profile's `display_name` is set to account_table.name.
 * - Sets user-level properties `project_uuid` and `project_title` (most recently
 *   visited hub) for HubSpot export — single value per user, overwrites on hub switch.
 * - Refs prevent redundant Mixpanel API calls when the project hasn't changed.
 */
export const useMixpanelProject = (): void => {
  const authContext = useContext(AuthContext)
  const user = authContext.user
  const details = useProjectStore((state: ProjectState) => state.details)

  const lastSetMixpanelProjectId = useRef<string | null>(null)
  const lastSetMixpanelAccountId = useRef<string | null>(null)

  useEffect(() => {
    if (!user) {
      const savedId = localStorage.getItem('public_id')
      if (savedId) {
        mixpanel.identify(savedId)
      } else {
        const id = crypto.randomUUID()
        localStorage.setItem('public_id', id)
        mixpanel.identify(id)
      }
    } else if (!authContext.userDetails?.yogi_user) {
      const hubUuid = details.proj_uuid
      if (hubUuid && hubUuid !== lastSetMixpanelProjectId.current) {
        mixpanel.setGroup('hub_uuid', hubUuid)
        mixpanel.setGroupProperties('hub_uuid', hubUuid, {
          display_name: details.title,
        })
        mixpanel.set({
          project_uuid: hubUuid,
          project_title: details.title,
        })
        lastSetMixpanelProjectId.current = hubUuid
      }
      const accountUuid = details.account_uuid
      if (accountUuid && accountUuid !== lastSetMixpanelAccountId.current) {
        mixpanel.setGroup('account_uuid', accountUuid)
        if (details.account_name) {
          mixpanel.setGroupProperties('account_uuid', accountUuid, {
            display_name: details.account_name,
          })
        }
        lastSetMixpanelAccountId.current = accountUuid
      }
    }
  }, [
    details.proj_uuid,
    details.title,
    details.account_uuid,
    details.account_name,
    authContext.userDetails,
    user,
  ])
}
